import axios from '@/_utils/axios';
import useFileType from '@/components/_hooks/useFileType';
import { AssetType, InventoryFileEntity } from '@/redux/slices/slice_types';
import { ContentPlaybackPresignUrlsResponse, ContentPlaybackResponse, ContentPlaybackTimelineAudioTrack, FileWithProxy } from './video-player.types';

import { useConfigValue } from '@/components/_hooks/useConfigValue';
import { OmakasePlayerClientConfig } from '@/__configs/config_types';
import { useState } from 'react';

type SourcesMap = Record<string, string>;

const useContentPlayback = () => {
  const getFileGroup = useFileType();
  const omakaseConfig = useConfigValue<OmakasePlayerClientConfig>(['omakasePlayerConfig']);
  const [dolbyAudioTracks, setDolbyAudioTracks] = useState<ContentPlaybackTimelineAudioTrack[]>([]);
  const [dolbyVideoUrl, setDolbyVideoUrl] = useState<string | undefined>();
  const [sourceUrls, setSourceUrls] = useState<SourcesMap>({});

  const getFileInstanceUri = (file: InventoryFileEntity) => {
    const instances = Object.values(file.instances);
    return instances[0]?.uri || '';
  };

  const getContentPlayback = async (files: InventoryFileEntity[], optional?: { videoUrl: string }) => {
    try {
      const grouping: { [key: string]: string[] } = {
        video: [],
        audio: [],
        subtitle: []
      };

      files.forEach((file) => {
        const instanceUri = getFileInstanceUri(file);
        if (instanceUri) {
          grouping[(file.file_category || getFileGroup(file.file_type)) as keyof typeof grouping]?.push(instanceUri);
        }
      });

      const payload = {
        urls: {
          video: optional?.videoUrl ? [optional.videoUrl] : grouping.video,
          audio: grouping.audio,
          subtitle: grouping.subtitle
        },
        options: {
          DOLBY_RASP: {
            options: {}
          }
        }
      };
      const response = await axios.post<ContentPlaybackPresignUrlsResponse>('/inventory/content-playback/', payload);

      return response.data.data;
    } catch (error) {
      console.error('Error fetching signed URLs:', error);
    }
  };

  const fetchDolbyRaspJson = async (url: string | undefined): Promise<ContentPlaybackResponse | undefined> => {
    if (!url) {
      return Promise.resolve(undefined);
    }

    const response = await fetch(url);
    return response.json();
  };

  const getAudioTracks = (contentPlayback?: ContentPlaybackResponse): ContentPlaybackTimelineAudioTrack[] => {
    if (!contentPlayback) {
      return [];
    }

    return contentPlayback.presentation.timeline.tracks.filter((t) => t.type === 'audio');
  }

  const getVideoUrl = (contentPlayback?: ContentPlaybackResponse): string | undefined => {
    if (!contentPlayback) {
      return;
    }

    return contentPlayback.media.main[0]?.url;
  }

  const fetchSource = async (previewFileArg: InventoryFileEntity, rootTitleId: number): Promise<string | undefined> => {
    if (previewFileArg?.is_rally_inventory === false && (previewFileArg?.non_rally_inventory_download_url || '').length > 0) {
      return Promise.resolve(previewFileArg?.non_rally_inventory_download_url as string);
    }

    try {
      const res = await axios.get(`/inventory/titles/${rootTitleId}/files/${previewFileArg.id}/`);
      if (!res?.data?.success) throw new Error();
      return res.data?.data?.url;
    } catch (error) {
      console.error('Error fetching file source URL:', error);
      return undefined;
    }
  };

  const mapFilesFromFilesApi = async (files: InventoryFileEntity[], rootTitleId: number) => {
    const sourcesMap: SourcesMap = {};
    const fileSources = await Promise.all(files.map((file) => fetchSource(file, rootTitleId)));
    files.forEach((file, index) => {
      sourcesMap[file.id] = fileSources[index] || '';
    });

    return sourcesMap;
  };

  const getSourceFilesMap = (signedUrls: ContentPlaybackPresignUrlsResponse['data']['PRESIGN_URLS'], files: InventoryFileEntity[]) => {
    const sourcesMap: SourcesMap = {};

    const indexes = {
      video: 0,
      audio: 0,
      subtitle: 0
    };

    files.forEach((file) => {
      const section = file.file_category || getFileGroup(file.file_type);
      const uris = section && signedUrls ? signedUrls[section as keyof ContentPlaybackPresignUrlsResponse['data']['PRESIGN_URLS']] : null;
      if (uris === null || uris.length === 0) {
        return;
      }
      if (['audio', 'video'].includes(section || '') && uris?.length) {
        sourcesMap[file.id] = uris[indexes[section as keyof typeof indexes]++] || '';
      } else {
        sourcesMap[file.id] = uris[indexes.subtitle++] || '';
      }
    });

    return sourcesMap;
  };

  const getPlaybackSourceUrls = async (
    files: (InventoryFileEntity & { section?: string })[] = [],
    rootTitleId: number,
  ): Promise<SourcesMap> => {
    let sourcesMap: SourcesMap = {};

    if (omakaseConfig?.enableContentPlayback) {
      const contentPlayback = await getContentPlayback(files);
      const dolbyRasp = await fetchDolbyRaspJson(contentPlayback?.DOLBY_RASP);
      const audioTracks = getAudioTracks(dolbyRasp);
      const videoUrl = getVideoUrl(dolbyRasp);
      setDolbyAudioTracks(audioTracks);
      setDolbyVideoUrl(videoUrl);

      if (!contentPlayback?.PRESIGN_URLS) {
        return sourcesMap;
      }

      sourcesMap = getSourceFilesMap(contentPlayback?.PRESIGN_URLS, files);
    } else {
      sourcesMap = await mapFilesFromFilesApi(files, rootTitleId);
    }
    setSourceUrls(sourcesMap);
    return sourcesMap;
  };

  const getPlaybackSourceUrlsWithVideoOverride = async (
    files: (InventoryFileEntity & { section?: string })[] = [],
    rootTitleId: number,
    videoUrl: string,
  ): Promise<{ sourcesMap: SourcesMap; videoUrlFromDolby?: string; audioTracks?: ContentPlaybackTimelineAudioTrack[] }> => {
    let sourcesMap: SourcesMap = {};

    if (omakaseConfig?.enableContentPlayback) {
      const contentPlayback = await getContentPlayback(files, { videoUrl });
      const dolbyRasp = await fetchDolbyRaspJson(contentPlayback?.DOLBY_RASP);
      const audioTracks = getAudioTracks(dolbyRasp);
      const videoUrlFromDolby = getVideoUrl(dolbyRasp);

      if (!contentPlayback?.PRESIGN_URLS) {
        return { sourcesMap };
      }

      sourcesMap = getSourceFilesMap(contentPlayback?.PRESIGN_URLS, files);

      return { 
        sourcesMap,
        videoUrlFromDolby,
        audioTracks,
      }
    } else {
      sourcesMap = await mapFilesFromFilesApi(files, rootTitleId);
    }
    return { sourcesMap };
  };

  const getPlaybackSourceUrlsAsFiles = async (
    files: (InventoryFileEntity & { section?: string })[] = [],
    rootTitleId: number,
    videoUrl: string,
  ): Promise<{ filesWithProxy: FileWithProxy[]; videoUrlFromDolby?: string; audioTracks?: ContentPlaybackTimelineAudioTrack[] }> => {
    const { sourcesMap, videoUrlFromDolby, audioTracks } = await getPlaybackSourceUrlsWithVideoOverride(files, rootTitleId, videoUrl);

    const filesWithProxy = files
      ?.filter((file) => file.section !== 'video')
      ?.map((file) => ({
        ...file,
        asset_type: (file.section || 'other') as AssetType,
        proxy: {
          url: sourcesMap[file.id],
          filename: file.name
        }
      }));
    
    return {
      filesWithProxy,
      videoUrlFromDolby,
      audioTracks
    };
  };

  return {
    dolbyAudioTracks,
    dolbyVideoUrl,
    sourceUrls,
    getContentPlayback,
    getPlaybackSourceUrls,
    getPlaybackSourceUrlsAsFiles
  };
};

export default useContentPlayback;
